import {t} from '../../i18n/i18n.js';
import {FieldType} from '../../core/vocabulary.js';

/**
 * Shows the stored file of a prefilled upload field next to its drop zone: a download link
 * for a document, or a thumbnail with its file name and a download link for an image, whose
 * drop zone then offers to replace it. The input keeps the stored path in
 * `data-existing-path` and stops being required.
 *
 * @param {HTMLInputElement} input File input rendered for an image or document field
 * @param {string} value Stored path or file name
 * @param {(name: string, detail: Object) => void} emit Emits `asset-request` (thumbnail) and
 *   `asset-open-request` (download)
 */
export function showExistingAsset(input, value, emit) {
    input.dataset.existingPath = value;
    const filename = String(value).split('/').pop();
    const fieldType = input.dataset.fieldType;
    const fieldGroup = input.closest('.field-group');
    const wrapper = fieldGroup.querySelector('.file-upload-wrapper');
    const removeAction = fieldGroup.querySelector('.asset-remove');
    if (removeAction) removeAction.hidden = false;

    if (fieldType === FieldType.DOCUMENT) {
        const existingHint = fieldGroup.querySelector('.current-file');
        if (existingHint) {
            existingHint.remove();
        }

        const hint = document.createElement('div');
        hint.className = 'current-file';

        const link = document.createElement('button');
        link.type = 'button';
        link.className = 'current-asset-link';
        link.textContent = `\u2193 ${filename}`;
        link.addEventListener('click', () => {
            emit('asset-open-request', {path: value, filename});
        });
        hint.appendChild(link);

        wrapper.insertAdjacentElement('afterend', hint);
    } else {
        const existingHint = fieldGroup.querySelector('.current-image');
        if (existingHint) {
            existingHint.remove();
        }

        const hint = document.createElement('div');
        hint.className = 'current-image';

        const img = document.createElement('img');
        img.className = 'current-image-thumb';
        img.alt = filename;
        img.onerror = () => { img.style.display = 'none'; };
        hint.appendChild(img);

        emit('asset-request', {path: value, img});

        const meta = document.createElement('div');
        meta.className = 'current-image-meta';

        const label = document.createElement('span');
        label.className = 'current-image-label';
        label.textContent = t('pageForm.currentImage');
        meta.appendChild(label);

        const nameSpan = document.createElement('span');
        nameSpan.className = 'current-image-filename';
        nameSpan.textContent = filename;
        nameSpan.title = filename;
        meta.appendChild(nameSpan);

        const link = document.createElement('button');
        link.type = 'button';
        link.className = 'current-asset-link';
        const linkIcon = document.createElement('i');
        linkIcon.className = 'fa-solid fa-download';
        link.appendChild(linkIcon);
        link.appendChild(document.createTextNode(` ${t('pageForm.download')}`));
        link.addEventListener('click', () => {
            emit('asset-open-request', {path: value, filename});
        });
        meta.appendChild(link);

        hint.appendChild(meta);

        const cardBody = wrapper.closest('.image-card-body');
        if (cardBody) {
            cardBody.appendChild(hint);
            const cardHeader = fieldGroup.querySelector('.image-card-header');
            if (cardHeader) cardHeader.setAttribute('aria-expanded', 'true');
        } else {
            wrapper.insertAdjacentElement('afterend', hint);
        }

        // Reframe the drop zone so the user understands that an image is
        // already saved and that uploading replaces it (rather than the
        // empty-state copy implying nothing's there yet).
        const dropTextEl = wrapper.querySelector('.file-upload-text');
        const dropNameEl = wrapper.querySelector('.file-upload-name');
        if (dropTextEl) dropTextEl.textContent = t('pageForm.uploadReplace');
        if (dropNameEl) dropNameEl.textContent = '';
    }

    input.removeAttribute('required');
}

/**
 * Forgets the stored file of an upload field: drops `data-existing-path`, removes the shown
 * thumbnail or download link and restores the empty drop zone text.
 *
 * @param {HTMLInputElement} input File input rendered for an image or document field
 */
export function clearExistingAsset(input) {
    delete input.dataset.existingPath;
    const fieldGroup = input.closest('.field-group');
    fieldGroup.querySelector('.current-image, .current-file')?.remove();
    fieldGroup.querySelector('.file-upload-text').textContent =
        t(input.dataset.fieldType === FieldType.DOCUMENT ? 'pageForm.uploadDocumentClick' : 'pageForm.uploadClick');
    fieldGroup.querySelector('.file-upload-name').textContent = t('pageForm.noFileSelected');
}
